import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "./../component/Header";
import Footer from "./../component/Footer";
import HeadingSkeleton from "./../component/HeadingSkeleton";
import OrdersTableSkeleton from "./../component/OrdersTableSkeleton";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { API_BASE_URL } from "../utils/app_url";

const MyOrders = () => {
  const { user } = useAuth();
  const { selectedLanguage, langJson } = useLanguage();

  const [loading, setLoading] = useState(true);
  const [allDetails, setAllDetails] = useState();
  const [selectedStore, setSelectedStore] = useState(0);
  const [selectedPhotoStore, setSelectedPhotoStore] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortKey, setSortKey] = useState("date-desc");

  // Language to locale mapping
  const localeMap = {
    en: "en-US",
    de: "de-DE",
    es: "es-ES",
    fr: "fr-FR",
    it: "it-IT",
    ru: "ru-RU",
    tr: "tr-TR",
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";

    try {
      const date = new Date(dateString);
      const locale = localeMap[selectedLanguage] || "en-US";

      return date.toLocaleDateString(locale, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch (error) {
      return dateString;
    }
  };

  const getStatusTagClass = (status) => {
    switch (status?.toLowerCase()) {
      case "delivered":
        return "fs-tag--success";

      case "processing":
        return "fs-tag--warning";

      case "new":
        return "fs-tag--critical";

      case "cancelled":
        return "fs-tag--info";

      default:
        return "fs-tag--info";
    }
  };

  const getPaymentMethodDisplay = (paymentMethod) => {
    if (!paymentMethod) return "";

    const methodMap = {
      "cash-on-delivery": "cashondelivery",
      "pre-payment": "prepayment",
      "as-agreed": "asagreed",
      paypal: "paypal",
    };

    const key = methodMap[paymentMethod?.toLowerCase()];

    return key ? langJson?.[key] : paymentMethod;
  };

  const fetchOrders = async () => {
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}order`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user?.authorisation?.token}`,
        },
      });

      const responseData = await response.json();

      if (responseData.res === true) {
        setAllDetails(responseData.data);
      }

      setLoading(false);
    } catch (error) {
      console.error("Error fetching orders:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  /*
   * Store name has been removed from the search.
   *
   * Existing search functionality remains:
   * - Order number
   * - Billing/customer name
   */
  const filterOrders = (orders) => {
    if (!orders) return [];

    return orders.filter((item) => {
      const searchLower = searchTerm.toLowerCase();

      const orderNumber =
        item?.order?.order_number?.toLowerCase() || "";

      const billName =
        item?.order?.name?.toLowerCase() || "";

      return (
        orderNumber.includes(searchLower) ||
        billName.includes(searchLower)
      );
    });
  };

  const sortOrders = (orders) => {
    if (!orders) return [];

    const sortedOrders = [...orders];

    switch (sortKey) {
      case "date-asc":
        return sortedOrders.sort(
          (a, b) =>
            new Date(a?.order?.ordered_date) -
            new Date(b?.order?.ordered_date)
        );

      case "date-desc":
        return sortedOrders.sort(
          (a, b) =>
            new Date(b?.order?.ordered_date) -
            new Date(a?.order?.ordered_date)
        );

      case "amount-asc":
        return sortedOrders.sort(
          (a, b) =>
            Number(a?.order?.total_amount) -
            Number(b?.order?.total_amount)
        );

      case "amount-desc":
        return sortedOrders.sort(
          (a, b) =>
            Number(b?.order?.total_amount) -
            Number(a?.order?.total_amount)
        );

      default:
        return sortedOrders;
    }
  };

  const filteredAndSortedOrders = sortOrders(
    filterOrders(allDetails?.orders)
  );

  const filteredAndSortedPhotoOrders = sortOrders(
    filterOrders(allDetails?.photo_orders)
  );

  return (
    <div>
      <Header />

      <style>{`
        body {
          background: #FFF;
        }
      `}</style>

      <div className="mainDiv">
        <div className="acctHdn">

          {/* =========================
              NORMAL ORDERS
          ========================== */}

          <div className="hdnSec topNew">
            {loading ? (
              <HeadingSkeleton width="180px" height="34px" />
            ) : (
              <h2 className="storeTabHdn orderHdnNew">
                {langJson?.myorders}
              </h2>
            )}
          </div>

          {!loading &&
            (allDetails?.orders?.length > 0 ||
              allDetails?.photo_orders?.length > 0) && (
              <div
                className="orderFiltersSection"
                style={{
                  display: "flex",
                  gap: "15px",
                  marginBottom: "20px",
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <input
                  type="text"
                  placeholder={langJson?.search || "Search..."}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    padding: "8px 12px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    flex: "1 1 200px",
                    minWidth: "150px",
                  }}
                />

                <select
                  value={sortKey}
                  onChange={(e) => setSortKey(e.target.value)}
                  style={{
                    padding: "8px 12px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    backgroundColor: "#fff",
                  }}
                >
                  <option value="date-desc">
                    {langJson?.newestfirst || "Newest First"}
                  </option>

                  <option value="date-asc">
                    {langJson?.oldest || "Oldest First"}
                  </option>

                  <option value="amount-desc">
                    {langJson?.pricehightolow ||
                      "Amount: High to Low"}
                  </option>

                  <option value="amount-asc">
                    {langJson?.pricelowtohigh ||
                      "Amount: Low to High"}
                  </option>
                </select>
              </div>
            )}

          {loading ? (
            <OrdersTableSkeleton columns={6} rows={4} />
          ) : allDetails?.orders?.length > 0 ? (
            <div className="myaccountDiv orderDiv">

              <ul className="tableHdn">
                <li>{langJson?.orderno}</li>
                <li>{langJson?.orderdate}</li>
                <li>{langJson?.billtoname}</li>
                <li>{langJson?.total}</li>
                <li>{langJson?.status}</li>
                <li className="actionHdn">
                  {langJson?.action}
                </li>
              </ul>

              {filteredAndSortedOrders?.length > 0 ? (
                filteredAndSortedOrders.map((item, index) => (
                  <div
                    className={
                      selectedStore === index
                        ? "orderMain orderMainActive"
                        : "orderMain"
                    }
                    key={index}
                  >
                    <ul className="orderSummaryRow">

                      <li>
                        {item?.order?.order_number}
                      </li>

                      <li>
                        {formatDate(
                          item?.order?.ordered_date
                        )}
                      </li>

                      <li>
                        {item?.order?.name}
                      </li>

                      <li>
                        ₹{" "}
                        {item?.order?.total_amount}
                      </li>

                      <li>
                        <span
                          className={`fs-tag ${getStatusTagClass(
                            item?.order?.status
                          )}`}
                        >
                          {item?.order?.status}
                        </span>
                      </li>

                      <li
                        onClick={() =>
                          setSelectedStore(
                            selectedStore === index
                              ? null
                              : index
                          )
                        }
                        style={{
                          cursor: "pointer",
                        }}
                      >
                        {langJson?.viewdetails}
                      </li>

                    </ul>

                    {selectedStore === index ? (
                      <div className="orderDetailsWrapper">

                        <div className="orderTableWrapper">

                          <ul className="tableDesc">
                            <li>{langJson?.item}</li>
                            <li>{langJson?.size}</li>
                            <li>{langJson?.qty}</li>
                            <li>{langJson?.price}(₹)</li>
                            <li>{langJson?.total}(₹)</li>
                          </ul>

                          {item?.products?.map(
                            (chItem, chIndex) => {
                              const itemTotal =
                                (chItem?.price ?? 0) *
                                (chItem?.quantity ?? 0);

                              return (
                                <ul
                                  className="tableDesc"
                                  key={chIndex}
                                >
                                  <li>
                                    <Link
                                      to={`/product/${chItem?.product?.slug}`}
                                      style={{
                                        color: "#0066cc",
                                        textDecoration:
                                          "none",
                                      }}
                                    >
                                      {chItem?.product?.title}

                                      {chItem?.product
                                        ?.article_no && (
                                        <>
                                          <br />

                                          <span
                                            style={{
                                              fontSize:
                                                "12px",
                                              color:
                                                "#666",
                                            }}
                                          >
                                            [
                                            {
                                              chItem
                                                ?.product
                                                ?.article_no
                                            }
                                            ]
                                          </span>
                                        </>
                                      )}
                                    </Link>
                                  </li>

                                  <li>
                                    {chItem?.size}
                                  </li>

                                  <li className="qty">
                                    {chItem?.quantity}
                                  </li>

                                  <li>
                                    ₹ {chItem?.price}
                                  </li>

                                  <li>
                                    ₹{" "}
                                    {itemTotal.toFixed(2)}
                                  </li>
                                </ul>
                              );
                            }
                          )}

                        </div>

                        <div className="orderTotalsWrapper">

                          <div className="buttonList">

                            <div className="buttonBox">
                              {langJson?.totalquantity}

                              <strong>
                                {item?.order?.quantity}{" "}
                                {langJson?.pcs}
                              </strong>
                            </div>

                            <div className="buttonBox">
                              {langJson?.totalprice}

                              <strong>
                                ₹{" "}
                                {
                                  item?.order
                                    ?.total_amount
                                }
                              </strong>
                            </div>

                            {item?.order
                              ?.payment_method && (
                              <div className="buttonBox">
                                {langJson?.paymentmethod}

                                <strong>
                                  {getPaymentMethodDisplay(
                                    item?.order
                                      ?.payment_method
                                  )}
                                </strong>
                              </div>
                            )}

                          </div>

                        </div>
                      </div>
                    ) : null}
                  </div>
                ))
              ) : (
                <div
                  style={{
                    padding: "20px",
                    textAlign: "center",
                  }}
                >
                  {langJson?.noordersfound}
                </div>
              )}
            </div>
          ) : !loading ? (
            <div className="myaccountDiv orderDiv">
              <div className="noOrderDiv">
                {langJson?.noordersfound}
              </div>
            </div>
          ) : null}


          

        </div>
      </div>

      <Footer />
    </div>
  );
};

export default MyOrders;